import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { BsX } from 'react-icons/bs';
import { MdOutlineFileUpload } from 'react-icons/md';
import { PrimaryOutlineButton } from '../button/button-component';
import Styled from './upload-image-thumbnail-style';

/**
 * Upload Image Thumbnail component.
 * @param {object} props The props object.
 * @param {string} props.label The label to display.
 * @param {string} props.name The name of the input field.
 * @param {object} props.registerOptions The register options for the input field.
 * @param {boolean} props.loading The loading state.
 * @returns {JSX.Element} The Upload Image Thumbnail component.
 */
const UploadImageThumbnail = ({ label, name, registerOptions, loading }) => {
  const inputRef = useRef(null);
  const [image, setImage] = useState(null);

  const removeThumbnail = (onChange) => {
    setImage(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onChange(null);
  };

  const focusThumbnailPicker = () => {
    inputRef.current.click();
  };

  const selectThumbnail = async (event, onChange) => {
    const file = event.target.files[0];
    setImage(file);
    onChange(file);

    // Preview the selected image
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImage('');
    }
  };

  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={null}
      rules={registerOptions}
      render={({ field: { value, onChange } }) => (
        <Styled.MainContainer>
          <Styled.Label>{label}</Styled.Label>
          <Styled.AlignImageElements>
            {image || value ? (
              <Styled.Thumbnail src={image ?? value} alt="thumbnail" />
            ) : (
              <Styled.IconContainer>
                <Styled.NoThumbnail />
              </Styled.IconContainer>
            )}
            <PrimaryOutlineButton
              title="Upload New Image"
              margin="0 0 0 16px"
              icon={<MdOutlineFileUpload />}
              type="button"
              buttonHeight="30px"
              onClick={focusThumbnailPicker}
              disabled={loading}
            />
            {image !== null && inputRef.current !== null && (
              <PrimaryOutlineButton
                title="Remove"
                buttonHeight="30px"
                margin="0 0 0 16px"
                icon={<BsX />}
                type="button"
                onClick={() => removeThumbnail(onChange)}
                disabled={loading}
              />
            )}
          </Styled.AlignImageElements>
          <Styled.ThumbnailPicker
            type="file"
            ref={inputRef}
            accept="image/*"
            hidden
            onChange={(event) => selectThumbnail(event, onChange)}
          />
        </Styled.MainContainer>
      )}
    />
  );
};

UploadImageThumbnail.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  registerOptions: PropTypes.object,
  loading: PropTypes.bool,
};

UploadImageThumbnail.defaultProps = {
  label: 'Image Thumbnail',
  registerOptions: {},
  loading: false,
};

export default UploadImageThumbnail;
